import { count as counted } from '../core/words.js';
import { key } from './setup.js';
import { SHORTFALLS } from '../data/resources.js';

/**
 * Grading a banquet setup against its BEO.
 *
 * Five things are marked, and the weights are an opinion about what the job
 * actually is:
 *
 *   spacing  0.32  Tables on the anchors the diagram called for. This is the
 *                  skill. Everything else is counting.
 *   chairs   0.23  Eight on every table, on every table.
 *   linen    0.18  Right colour, on all of them.
 *   staging  0.10  The lines on the sheet that are not furniture: risers,
 *                  signage. You cannot set these yourself — somebody else is
 *                  holding them — so this mark is entirely a measure of whether
 *                  you went and got them covered.
 *   count    0.09  The right number of rounds in the room at all. Low weight
 *                  because a room with the right count in the wrong places has
 *                  not really got the count right, and spacing already says so.
 *   tidy     0.08  Nothing extra, and nothing on the dance floor.
 *
 * Lateness is a multiplier rather than a sixth component, because a room set
 * perfectly at half past seven for a seven o'clock door is not an 85% room. It
 * is a good room that arrived after the guests, and the number has to be able
 * to say so. A room can be filed late until its doors open, half an hour after
 * its setup time: at a point and a fifth a minute a perfect room is a B ten
 * minutes over, a C twenty over and a D at the doors, and the grade is what
 * its departments are paid in, so late costs standing in proportion too.
 *
 * Pure, and the breakdown comes back whole. A grade the player cannot see the
 * reasoning for is a grade they cannot learn from.
 */

export const GRADES = Object.freeze([
  { at: 0.92, letter: 'A', multiplier: 1 },
  { at: 0.8, letter: 'B', multiplier: 0.7 },
  { at: 0.65, letter: 'C', multiplier: 0.35 },
  { at: 0.45, letter: 'D', multiplier: -0.3 },
  // A room the guests walked into and turned around in. It costs you.
  { at: -Infinity, letter: 'F', multiplier: -0.8 },
]);

export const WEIGHTS = Object.freeze({
  spacing: 0.32,
  chairs: 0.23,
  linen: 0.18,
  staging: 0.1,
  count: 0.09,
  tidy: 0.08,
});

/** A point and a fifth a minute past the setup time, floored, so late still beats not showing up. */
export const LATE_PER_MINUTE = 0.012;
export const LATE_FLOOR = 0.34;

export function letterFor(score) {
  return GRADES.find((grade) => score >= grade.at) ?? GRADES[GRADES.length - 1];
}

/** The anchors a BEO's layout calls for. */
export function wantedAnchors(setupSpec) {
  const { col, row, cols, rows } = setupSpec.layout;
  const wanted = new Set();
  for (let c = col; c < col + cols; c += 1) {
    for (let r = row; r < row + rows; r += 1) wanted.add(key(c, r));
  }
  return wanted;
}

export function gradeSetup({
  spec,
  tables,
  anchorAt,
  minutesLate = 0,
  shortfalls = [],
  /**
   * How much of a stray the tidiness mark lets you off, 0..1.
   *
   * The one place a work class touches the sheet, and it touches exactly one of
   * the six marks. It scales the sins rather than adding to the score, so a
   * room with nothing out of place is graded identically at every tier: the
   * perk is worth something only to somebody who left a mess, which is the only
   * version of "better at tidying up" that is not just a bonus.
   *
   * Zero is the default and the default is the whole history of this function.
   */
  tidyForgiveness = 0,
}) {
  const wanted = wantedAnchors(spec);
  const required = spec.tableCount;
  const placed = tables.length;

  const onTarget = tables.filter((table) => wanted.has(table.key));
  const offTarget = tables.filter((table) => !wanted.has(table.key));
  const onDanceFloor = tables.filter((table) => anchorAt(table.key)?.keepClear);

  // --- spacing --------------------------------------------------------------
  const spacing = required ? onTarget.length / required : 1;

  // --- count ----------------------------------------------------------------
  const count = required ? Math.max(0, 1 - Math.abs(placed - required) / required) : 1;

  // --- chairs ---------------------------------------------------------------
  // Only tables that are where they belong can be correctly chaired. Eight
  // chairs on a table in the wrong place is not eight chairs of credit.
  const wantedChairs = spec.chairsPerTable;
  const chairs = required
    ? onTarget.reduce(
        (total, table) =>
          total + Math.max(0, 1 - Math.abs(table.seats - wantedChairs) / Math.max(wantedChairs, 1)),
        0
      ) / required
    : 1;

  // --- linen ----------------------------------------------------------------
  const linen = required
    ? onTarget.filter((table) => table.cover?.variant === spec.linen).length / required
    : 1;

  // --- staging --------------------------------------------------------------
  // Only the staging shortfalls count here. A room short of linen is marked on
  // its linen, not twice.
  const stagingCalls = shortfalls.filter((entry) => SHORTFALLS[entry.id]?.kind === 'staging');
  /*
   * Delivered, not resolved. Engineering signing the work order is not the same
   * event as the risers being in the room — the first is permission and the
   * second is a walk to the shop and back with them in your arms, and the room
   * is graded on what is in it.
   */
  const covered = stagingCalls.filter((entry) => entry.delivered).length;
  const staging = stagingCalls.length ? covered / stagingCalls.length : 1;

  // --- tidiness -------------------------------------------------------------
  // A table on the dance floor is worth two strays, because it is not clutter,
  // it is the one thing the staging notes explicitly told you not to do.
  const sins = (offTarget.length + onDanceFloor.length) * (1 - clamp01(tidyForgiveness));
  const tidy = Math.max(0, 1 - sins / Math.max(required, 1));

  // An empty room is not a tidy room. Without this, walking away from a BEO
  // entirely still banks the full tidiness mark for having left no mess, and
  // "did nothing" scores above "tried and made errors".
  const abandoned = placed === 0 && required > 0;
  const parts = abandoned
    ? { spacing: 0, chairs: 0, linen: 0, staging: 0, count: 0, tidy: 0 }
    : { spacing, chairs, linen, staging, count, tidy };

  /*
   * Marks that do not apply are dropped and the rest are renormalised, rather
   * than being awarded full credit.
   *
   * An event with no staging on it was scoring a free tenth, which made it
   * strictly easier to get an A on than an identical event that happened to
   * need a riser — and worse, it quietly shifted every grade boundary the
   * moment the staging mark was added. Renormalising keeps a score meaning the
   * same thing across events that are marked on different numbers of things.
   */
  const applicable = Object.entries(WEIGHTS).filter(
    ([name]) => name !== 'staging' || stagingCalls.length > 0
  );
  const total = applicable.reduce((sum, [, weight]) => sum + weight, 0);
  const base = applicable.reduce((sum, [name, weight]) => sum + parts[name] * weight, 0) / total;

  const late = Math.max(0, minutesLate);
  const punctuality = late > 0 ? Math.max(LATE_FLOOR, 1 - late * LATE_PER_MINUTE) : 1;
  const score = clamp01(base * punctuality);
  const grade = letterFor(score);

  return {
    score,
    base,
    punctuality,
    minutesLate: late,
    letter: grade.letter,
    multiplier: grade.multiplier,
    parts,
    breakdown: [
      line('Spacing', parts.spacing, `${onTarget.length}/${counted(required, 'round')} on the diagram`),
      line('Chairs', parts.chairs, `${wantedChairs} per table`),
      line('Linen', parts.linen, `${spec.linen}`),
      ...(stagingCalls.length
        ? [
            line(
              'Staging',
              parts.staging,
              `${covered}/${stagingCalls.length} covered: ${stagingCalls
                .map((entry) => SHORTFALLS[entry.id].label.toLowerCase())
                .join(', ')}`
            ),
          ]
        : []),
      line('Count', parts.count, `${placed} in the room, ${required} called for`),
      line(
        'Tidy',
        parts.tidy,
        abandoned
          ? 'the room was never set'
          : onDanceFloor.length
            ? `${onDanceFloor.length} on the dance floor`
            : offTarget.length
              ? `${offTarget.length} off the diagram`
              : 'nothing out of place'
      ),
      ...(late > 0 ? [line('Late', punctuality, `${counted(Math.round(late), 'minute')} past setup`)] : []),
    ],
    staging: { called: stagingCalls.length, covered },
    counts: {
      placed,
      required,
      onTarget: onTarget.length,
      offTarget: offTarget.length,
      onDanceFloor: onDanceFloor.length,
    },
  };
}

/**
 * Grading a strike: what got to the dock, and what went on the ground on the
 * way.
 *
 *   delivered  0.6  Of everything that was on the lawn, how much is on a cart
 *                   on the dock. The job.
 *   clean      0.4  What was never spilled, by mass rather than by count. A
 *                   table cart on its side is four rounds and a hundred and
 *                   forty kilos; a chair cart is two dozen chairs and ninety.
 *                   Mass is the number that says which of those is the worse
 *                   afternoon, the same as everywhere else. Picking it all back
 *                   up gets the load delivered and does not un-spill it: the
 *                   crew saw it go over.
 *
 * Lateness is the same multiplier a room is marked with, for the same reason.
 */
export const TEARDOWN_WEIGHTS = Object.freeze({ delivered: 0.6, clean: 0.4 });

/** A quarter of the load's mass on the ground, and the clean mark is gone. */
export const SPILL_SCALE = 4;

export function gradeTeardown({ total, delivered, totalMass, spilledMass = 0, spilled = 0, minutesLate = 0 }) {
  const parts = {
    delivered: total ? Math.min(1, delivered / total) : 1,
    clean: totalMass ? Math.max(0, 1 - (spilledMass / totalMass) * SPILL_SCALE) : 1,
  };
  // Nothing brought in is not a clean job; it is no job.
  if (!delivered) parts.clean = 0;
  const base = Object.entries(TEARDOWN_WEIGHTS).reduce((sum, [name, weight]) => sum + parts[name] * weight, 0);
  const late = Math.max(0, minutesLate);
  const punctuality = late > 0 ? Math.max(LATE_FLOOR, 1 - late * LATE_PER_MINUTE) : 1;
  const score = clamp01(base * punctuality);
  const grade = letterFor(score);
  return {
    score,
    base,
    punctuality,
    minutesLate: late,
    letter: grade.letter,
    multiplier: grade.multiplier,
    parts,
    breakdown: [
      line('Delivered', parts.delivered, `${delivered}/${total} of the lawn on the dock`),
      line('Clean', parts.clean, spilled ? `${spilled} spilled on the way` : delivered ? 'nothing went over' : 'nothing came in'),
      ...(late > 0 ? [line('Late', punctuality, `${counted(Math.round(late), 'minute')} past due`)] : []),
    ],
    counts: { total, delivered, spilled },
  };
}

function line(label, value, detail) {
  return { label, value, detail };
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

/**
 * What a grade does to the departments.
 *
 * The BEO's award is the A-grade number; every other letter scales it, and D
 * and F go negative. The rivalry matrix does the rest — nothing here knows the
 * name of a rival, it just hands Banquets a number and lets the consequences
 * fall where the data says they fall.
 */
export function awardFor(beo, grade) {
  return Object.fromEntries(
    Object.entries(beo.award ?? {}).map(([department, amount]) => [
      department,
      Math.round(amount * grade.multiplier * 10) / 10,
    ])
  );
}
